import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { I18nProvider } from "./contexts/I18nContext";
import { CreatorConfigProvider, useCreatorConfig, NAV_KEY_TO_PATH } from "./contexts/CreatorConfigContext";
import { ToastContainer } from "./components/ui/Toast";
import { DashboardSkeleton } from "./components/ui/Skeleton";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import NewOrder from "./pages/NewOrder";
import OrderDetail from "./pages/OrderDetail";
import Products from "./pages/Products";
import Materials from "./pages/Materials";
import Production from "./pages/Production";
import Customers from "./pages/Customers";
import CustomerCare from "./pages/CustomerCare";
import Insights from "./pages/Insights";
import Suppliers from "./pages/Suppliers";
import Debts from "./pages/Debts";
import Delivery from "./pages/Delivery";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import CustomerOrder from "./pages/CustomerOrder";
import Finance from "./pages/Finance";
import CreatorPanel from "./pages/CreatorPanel";
import api from "./lib/api";

// ─── ProtectedRoute: chặn chưa đăng nhập ────────────────────
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <DashboardSkeleton />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

// ─── BlockedRoute: chặn trang bị Creator ẩn ─────────────────
// Nếu trang nằm trong hidden_nav → redirect về dashboard
function BlockedRoute({ navKey, children }) {
  const { config, loading } = useCreatorConfig();
  const location = useLocation();

  // Trong lúc đang fetch config → chờ, không redirect vội
  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <DashboardSkeleton />
      </div>
    );
  }

  if (config.hidden_nav.includes(navKey)) {
    return <Navigate to="/dashboard" replace state={{ from: location }} />;
  }

  return children;
}

// ─── IdleLogout: tự đăng xuất sau 1 tiếng không thao tác ────
const IDLE_TIMEOUT = 60 * 60 * 1000; // 1 tiếng = 3,600,000ms

function IdleLogout() {
  const { user, logout } = useAuth();
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    if (!user) return;

    const reset = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        await logout();
        window.location.href = "/login?reason=idle";
      }, IDLE_TIMEOUT);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset(); // bắt đầu đếm ngay khi mount

    return () => {
      clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  return null;
}
function KickPoller() {
  const { user, logout } = useAuth();
  React.useEffect(() => {
    if (!user) return;
    const poll = async () => {
      try {
        const { data } = await api.get("/auth/check-kicked");
        if (data?.kicked) {
          await logout();
          window.location.href = "/login";
        }
      } catch {}
    };
    poll(); // kiểm tra ngay khi mount
    const id = setInterval(poll, 30000); // sau đó mỗi 30 giây
    return () => clearInterval(id);
  }, [user, logout]);
  return null;
}

// ─── AppRouter ────────────────────────────────────────────────
function AppRouter() {
  return (
    <>
      <KickPoller />
      <IdleLogout />
      <Routes>
      {/* CREATOR PANEL — auth riêng, không cần đăng nhập admin */}
      <Route path="/creator" element={<CreatorPanel />} />

      {/* ROUTE KHÁCH HÀNG — công khai */}
      <Route path="/dat-hang" element={<CustomerOrder />} />

      {/* Route hệ thống */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* ROUTE BẢO VỆ — phải đăng nhập + không bị Creator ẩn */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* dashboard không bao giờ bị ẩn */}
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Các trang có thể bị Creator ẩn */}
        <Route path="/orders" element={<BlockedRoute navKey="orders"><Orders /></BlockedRoute>} />
        <Route path="/orders/new" element={<BlockedRoute navKey="orders"><NewOrder /></BlockedRoute>} />
        <Route path="/orders/:id" element={<BlockedRoute navKey="orders"><OrderDetail /></BlockedRoute>} />
        <Route path="/products" element={<BlockedRoute navKey="products"><Products /></BlockedRoute>} />
        <Route path="/materials" element={<BlockedRoute navKey="materials"><Materials /></BlockedRoute>} />
        <Route path="/production" element={<BlockedRoute navKey="production"><Production /></BlockedRoute>} />
        <Route path="/customers" element={<BlockedRoute navKey="customers"><Customers /></BlockedRoute>} />
        <Route path="/customer-care" element={<BlockedRoute navKey="customerCare"><CustomerCare /></BlockedRoute>} />
        <Route path="/insights" element={<BlockedRoute navKey="insights"><Insights /></BlockedRoute>} />
        <Route path="/suppliers" element={<BlockedRoute navKey="suppliers"><Suppliers /></BlockedRoute>} />
        <Route path="/debts" element={<BlockedRoute navKey="debts"><Debts /></BlockedRoute>} />
        <Route path="/delivery" element={<BlockedRoute navKey="delivery"><Delivery /></BlockedRoute>} />
        <Route path="/users" element={<BlockedRoute navKey="users"><Users /></BlockedRoute>} />
        <Route path="/reports" element={<BlockedRoute navKey="reports"><Reports /></BlockedRoute>} />
        <Route path="/finance" element={<BlockedRoute navKey="finance"><Finance /></BlockedRoute>} />
        <Route path="/settings" element={<BlockedRoute navKey="settings"><Settings /></BlockedRoute>} />
      </Route>

      {/* Link sai → về dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CreatorConfigProvider>
          <BrowserRouter>
            <AppRouter />
            <ToastContainer />
          </BrowserRouter>
        </CreatorConfigProvider>
      </AuthProvider>
    </I18nProvider>
  );
}